import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CalendarDays, Check, Plus, X as XIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import {
  useDecideLeaveMutation,
  useFileLeaveRequestMutation,
  useLeaveRequestsQuery,
  useLeaveTypesQuery,
} from '@/client/api/hr/people';
import { HrPageShell } from './components/HrPageShell';
import { EmployeeAvatar, EmployeeLink, EmptyState, RowMenu, StatusBadge } from './components/hr-ui';
import { LeaveDecisionDialog } from './components/LeaveDecisionDialog';
import { formatDay, formatRange } from './components/leave-format';
import { useEmployees } from './hr-store';
import {
  hasLeft,
  TIME_OFF_STATUS_BADGE,
  TIME_OFF_STATUS_LABEL,
  type TimeOffRequest,
  type TimeOffStatus,
} from '@/client/types/hr';

const inputClass =
  'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

const STATUS_FILTERS: (TimeOffStatus | 'all')[] = ['pending', 'approved', 'denied', 'cancelled', 'all'];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      {children}
    </label>
  );
}

type FormState = { employeeId: string; typeId: string; startDate: string; endDate: string; reason: string };
const emptyForm: FormState = { employeeId: '', typeId: '', startDate: '', endDate: '', reason: '' };

const TimeOffRequestsPage = () => {
  const employees = useEmployees();
  const requestsQ = useLeaveRequestsQuery();
  const types = (useLeaveTypesQuery().data ?? []).filter((t) => t.status === 'active');
  const file = useFileLeaveRequestMutation();
  const decide = useDecideLeaveMutation();
  const canApprove = useGate(PERMISSIONS.hr.leaveApprove).allowed;
  const [statusFilter, setStatusFilter] = useState<TimeOffStatus | 'all'>('pending');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [deciding, setDeciding] = useState<{ request: TimeOffRequest; decision: 'approve' | 'deny' } | null>(null);

  const requests = requestsQ.data ?? [];
  const filtered = useMemo(
    () => requests.filter((r) => statusFilter === 'all' || r.status === statusFilter),
    [requests, statusFilter],
  );
  const employeeById = (id?: string | null) => employees.find((e) => e.id === id) ?? null;

  const today = new Date().toISOString().slice(0, 10);
  const stats = useMemo(
    () => [
      { key: 'pending', label: 'Waiting for a decision', value: requests.filter((r) => r.status === 'pending').length },
      {
        key: 'out',
        label: 'Out today',
        value: requests.filter((r) => r.status === 'approved' && r.startDate <= today && r.endDate >= today).length,
      },
      {
        key: 'upcoming',
        label: 'Approved, upcoming',
        value: requests.filter((r) => r.status === 'approved' && r.startDate > today).length,
      },
      {
        key: 'days',
        label: `Days approved in ${today.slice(0, 4)}`,
        value: requests
          .filter((r) => r.status === 'approved' && r.startDate.startsWith(today.slice(0, 4)))
          .reduce((n, r) => n + r.days, 0),
      },
    ],
    [requests, today],
  );

  const openFile = () => {
    setForm({ ...emptyForm, typeId: types[0]?.id ?? '' });
    setDialogOpen(true);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.employeeId || !form.typeId || !form.startDate || !form.endDate) {
      toast.error('Employee, type, start and end are required');
      return;
    }
    file.mutate(
      { employeeId: form.employeeId, typeId: form.typeId, startDate: form.startDate, endDate: form.endDate, reason: form.reason.trim() },
      {
        onSuccess: (r) => {
          toast.success(`Filed: ${r.days} working day${r.days === 1 ? '' : 's'}, waiting for a decision`);
          setDialogOpen(false);
          setStatusFilter('pending');
        },
        onError: (err) => toast.error((err as Error).message || 'Could not file the request'),
      },
    );
  };

  const confirmDecision = (note: string) => {
    if (!deciding) return;
    const { request, decision } = deciding;
    decide.mutate(
      { id: request.id, decision, note },
      {
        onSuccess: () => {
          toast.success(decision === 'approve' ? 'Approved' : 'Denied');
          setDeciding(null);
        },
        onError: (err) => toast.error((err as Error).message || 'Could not record the decision'),
      },
    );
  };

  return (
    <HrPageShell
      mock={false}
      title="Time off requests"
      description="Leave requests across the team. Managers decide their own reports' requests in My workplace; holders of Approve time off can decide any of them here."
      crumbs={[{ label: 'Human Resource', href: '/admin/human-resource' }, { label: 'Time Off' }, { label: 'Requests' }]}
      icon={CalendarDays}
      actions={
        <PermissionButton code={PERMISSIONS.hr.manage} className="gap-2" onClick={openFile}>
          <Plus className="h-4 w-4" /> File for someone
        </PermissionButton>
      }
      stats={stats}
    >
      <div className="mb-4 inline-flex flex-wrap rounded-lg border border-gray-200 bg-white p-1">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              statusFilter === s ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {s === 'all' ? 'All' : TIME_OFF_STATUS_LABEL[s]}
            {s === 'pending' && stats[0].value > 0 && ` (${stats[0].value})`}
          </button>
        ))}
      </div>

      {requestsQ.isError ? (
        <EmptyState label={`Could not load requests: ${(requestsQ.error as Error)?.message ?? 'unknown error'}`} />
      ) : requestsQ.isLoading ? (
        <EmptyState label="Loading time off requests…" />
      ) : filtered.length === 0 ? (
        <EmptyState label={statusFilter === 'pending' ? 'Nothing is waiting for a decision.' : 'No requests match this filter.'} />
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => {
            const employee = employeeById(r.employeeId);
            const decider = employeeById(r.decidedBy);
            return (
              <div key={r.id} className="flex items-center gap-4 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                <EmployeeAvatar employee={employee} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm">
                    <EmployeeLink employee={employee} />
                  </p>
                  <p className="text-xs text-gray-500">
                    {r.typeName} · {formatRange(r)} · {r.days} working day{r.days === 1 ? '' : 's'}
                  </p>
                  {r.reason && <p className="mt-1 text-xs text-gray-500">“{r.reason}”</p>}
                  <p className="mt-1 text-[11px] text-gray-400">
                    Requested {formatDay(r.requestedAt)}
                    {r.decidedAt &&
                      ` · ${r.status === 'approved' ? 'Approved' : 'Denied'} by ${decider?.name ?? 'HR'} on ${formatDay(r.decidedAt)}`}
                    {r.decisionNote && ` — “${r.decisionNote}”`}
                  </p>
                </div>
                <StatusBadge label={TIME_OFF_STATUS_LABEL[r.status]} className={TIME_OFF_STATUS_BADGE[r.status]} />
                <RowMenu
                  label={`Actions for ${employee?.name ?? 'request'}`}
                  items={[
                    {
                      label: 'Approve',
                      icon: Check,
                      onSelect: () => setDeciding({ request: r, decision: 'approve' }),
                      hidden: !canApprove || r.status !== 'pending',
                    },
                    {
                      label: 'Deny',
                      icon: XIcon,
                      danger: true,
                      onSelect: () => setDeciding({ request: r, decision: 'deny' }),
                      hidden: !canApprove || r.status !== 'pending',
                    },
                  ]}
                />
              </div>
            );
          })}
        </div>
      )}

      <LeaveDecisionDialog
        pending={deciding}
        who={employeeById(deciding?.request.employeeId)?.name ?? 'This employee'}
        busy={decide.isPending}
        onCancel={() => setDeciding(null)}
        onConfirm={confirmDecision}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>File time off for someone</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <Field label="Employee">
              <select value={form.employeeId} onChange={(e) => setForm((f) => ({ ...f, employeeId: e.target.value }))} className={inputClass}>
                <option value="">Select an employee</option>
                {employees
                  .filter((e) => !hasLeft(e))
                  .map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}
                    </option>
                  ))}
              </select>
            </Field>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Type">
                <select value={form.typeId} onChange={(e) => setForm((f) => ({ ...f, typeId: e.target.value }))} className={inputClass}>
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="First day">
                <input type="date" value={form.startDate} onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))} className={inputClass} />
              </Field>
              <Field label="Last day">
                <input
                  type="date"
                  value={form.endDate}
                  min={form.startDate || undefined}
                  onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                  className={inputClass}
                />
              </Field>
            </div>
            <Field label="Reason (optional)">
              <input value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} className={inputClass} />
            </Field>
            <p className="text-xs text-gray-500">Weekends are not counted. The request still needs a decision.</p>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={file.isPending}>
                {file.isPending ? 'Filing…' : 'File request'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </HrPageShell>
  );
};

export default TimeOffRequestsPage;
